import React, { useCallback } from 'react';

// helpers
import { approvalGroupsAPI } from 'api/approvalManagement/approvalGroupsAPI';
import { AutocompleteOption } from '@ui';
import { OnboardingEntryTypes } from 'enums/onboarding/crm';
import { userManagementAPINew } from 'api/accountManagement/userManagementAPINew';
import { ApprovalWorkflowType } from 'components/ModalDialogs/TemplateModalDialogs/Approval/ApprovalGroupModal';
import { IGroupParticipantShort } from 'typings/approvalManagement/accountManagement';
import { getFormattedContactName } from 'helpers/crmHelpers';
import { accountManagementRulesAPI } from 'api/accountManagement/accountManagementRulesAPI';

// components
import { FormAutocomplete } from '@ui';

interface IProps {
  approvalWorkflowType: ApprovalWorkflowType;
}

const ApprovalGroupParticipantsAutocomplete = ({
  approvalWorkflowType,
  ...rest
}: IProps) => {
  const getParticipantsBasedOnWorkflowType = async (
    workflowType: ApprovalWorkflowType,
    searchQuery: string,
    page: number,
    limit: number,
  ): Promise<{
    total: number;
    options: AutocompleteOption<IGroupParticipantShort>[];
  }> => {
    switch (workflowType) {
      case 'transactions': {
        const response = await approvalGroupsAPI.fetchApprovalGroupParticipants(
          {
            page,
            limit,
            userFullName: searchQuery,
          },
        );
        return {
          total: response.total,
          options: response.data.map((e) => ({
            id: e.userId,
            model: e,
            label: e.userFullName,
          })),
        };
      }

      case 'account-management-transactions': {
        const response =
          await accountManagementRulesAPI.fetchApprovalGroupParticipants({
            page,
            limit,
            userFullName: searchQuery,
          });

        return {
          total: response.total,
          options: response.data.map((e) => ({
            id: e.userId,
            model: e,
            label: e.userFullName,
          })),
        };
      }

      case 'onboarding-approval-group': {
        const response = await userManagementAPINew.fetchClientGroupMembers({
          page,
          limit,
          type: OnboardingEntryTypes.Contact,
          isPossibleMemberOfInitialApprovalGroup: true,
          search: searchQuery,
        });

        return {
          total: response.total,
          options: response.data.map((e) => {
            const userFullName = getFormattedContactName(
              e.contact.firstName,
              e.contact.lastName,
              e.contact.middleName,
            );

            return {
              id: e.id,
              model: {
                userId: e.contact.userId,
                userFullName,
              },
              label: userFullName,
            };
          }),
        };
      }
    }
  };

  const fetchData = useCallback(
    async (searchQuery: string, page: number, limit: number) => {
      const options = await getParticipantsBasedOnWorkflowType(
        approvalWorkflowType,
        searchQuery,
        page,
        limit,
      );
      return options;
    },
    [approvalWorkflowType],
  );

  return (
    <FormAutocomplete<IGroupParticipantShort>
      {...rest}
      mode="multiple"
      fetchData={fetchData}
    />
  );
};

export default ApprovalGroupParticipantsAutocomplete;
